import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import UploadedAccount from "../components/UploadedAccount";
import { setAccountName, useSynchronizeFinalMutation } from "../store";
import Button from "./Button";
import Link from "./Link";

const scrollToTop = () => window.scrollTo({ top: 0, left: 0, behavior: "smooth" });

function UploadedAccounts({ synchronizingData, refetchData, onAddFile = scrollToTop, embedded = false, title }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [synchronizeFinal] = useSynchronizeFinalMutation();
  const [confirmFailed, setConfirmFailed] = useState(false);
  const accountName = useSelector((state) => {
    return state.file.accountName;
  });

  const synchronizeAccount = (event) => {
    event.preventDefault();
    const data = synchronizingData?.synchronizingInvestingAccounts;
    let accounts = [];
    accounts = data.map((account) => {
      const updatedData = accountName.find(
        (item) => item.id === account.id && item.name !== account.name,
      );
      return updatedData
        ? updatedData
        : {
            id: account.id,
            name: account.name,
          };
    });
    setConfirmFailed(false);
    synchronizeFinal({
      accounts,
    })
      .unwrap()
      .then(() => {
        dispatch(setAccountName([]));
        window.location = "/reports";
      })
      .catch(() => setConfirmFailed(true));
  };

  const accounts = useMemo(() => {
    return synchronizingData?.synchronizingInvestingAccounts || [];
  }, [synchronizingData]);

  const hasSyncingReports = useMemo(() => {
    return accounts.some((account) =>
      account.brokerReports?.some((report) => report.status === "SYNCING")
    );
  }, [accounts]);

  useEffect(() => {
    if (!hasSyncingReports || !refetchData) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      refetchData();
    }, 3000);

    return () => clearInterval(intervalId);
  }, [hasSyncingReports, refetchData]);

  let content;
  if (synchronizingData) {
    content = accounts.map((account) => {
      return <UploadedAccount key={account.id} account={account} />;
    });
  }

  return (
    <>
      {content &&
        synchronizingData?.synchronizingInvestingAccounts.length > 0 && (
          <div className={embedded ? "" : "pl-16 pr-8 mt-20"}>
            <h4 className="text-lg mb-5">{title || t('upload.uploadedAccounts.title')}</h4>
            {content}
            {confirmFailed && (
              <p role="alert" className="text-sm text-red-600 mb-3">
                {t('upload.uploadedAccounts.confirmError')}
              </p>
            )}
            <div className="flex">
              <Link
                to="/reports"
                handleClick={synchronizeAccount}
                className={`flex justify-center items-center w-[200px] h-[60px] mb-5 border-transparent rounded-xl text-white bg-gradient-to-r from-cyan-650 to-cyan-75 hover:from-cyan-900 hover:to-cyan-650 ${hasSyncingReports ? "pointer-events-none opacity-50" : ""}`}
                tabIndex={hasSyncingReports ? -1 : 0}
              >
                {hasSyncingReports
                  ? t('upload.uploadedAccounts.syncing')
                  : t('upload.uploadedAccounts.confirm')}
              </Link>
              <Button
                onClick={onAddFile}
                className="w-[200px] h-[60px] justify-center border-transparent bg-clip-text text-transparent bg-gradient-to-r from-cyan-650 to-cyan-75 hover:from-cyan-900 hover:to-cyan-650"
                // to="/accounts"
              >
                {t('upload.uploadedAccounts.addFile')}
              </Button>
            </div>
          </div>
        )}
    </>
  );
}

export default UploadedAccounts;
